import React, { useState, useEffect } from 'react';
import {
    ArrowRight,
    ArrowLeft,
    Check,
    FileText,
    Users,
    User,
} from 'lucide-react';
import useRegistrationStore from '../store/useRegistrationStore';

const steps = [
    { num: 1, title: 'Authority Data' },
    { num: 2, title: 'Contact Details' },
];

export default function AuthorityContactForm() {
    const [step, setStep] = useState(1);

    // Get data and actions from Zustand store
    const {
        providerData,
        authorityData,
        updateAuthorityData
    } = useRegistrationStore();

    // Local form state synced with store
    const [form, setForm] = useState(authorityData);

    // Sync local state with store on mount
    useEffect(() => {
        setForm(authorityData);
    }, [authorityData]);

    // Check if provider data exists, redirect if not
    useEffect(() => {
        if (!providerData.facilityName) {
            window.location.href = '/#step-1';
        }
    }, [providerData]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Validate number-only fields (signatory and all department contact numbers)
        if (
            name === 'signatory1ContactNo' ||
            name.includes('Contact1')) {
            // Only allow digits
            if (value && !/^\d*$/.test(value)) {
                return; // Reject non-numeric input
            }
        }

        if (name === 'signatory1EmiratesId') {
            if (value && !/^[\d-]*$/.test(value)) return;
        }

        const updatedForm = { ...form, [name]: value };
        setForm(updatedForm);

        // Update store immediately for real-time persistence
        updateAuthorityData({ [name]: value });
    };

    const isValidEmiratesId = (eid) => {
        const regex = /^784-(19|20)\d{2}-\d{7}-\d$/;
        return regex.test(eid);
    };

    const handleFinish = () => {
        // Ensure all data is saved to store
        updateAuthorityData(form);
        // Navigate to review page
        window.location.href = '/#step-3';
    };

    const canProceed = () => {
        if (step === 1) {
            // All fields are mandatory except signatory1Remarks
            return (
                form.facilityLicenseNo.trim().length > 0 &&
                form.facilityLicenseExpiry.trim().length > 0 &&
                form.tradeLicNo.trim().length > 0 &&
                form.tradeLicName.trim().length > 0 &&
                form.tradeLicExpiry.trim().length > 0 &&
                form.signatory1Name.trim().length > 0 &&
                form.signatory1Designation.trim().length > 0 &&
                form.signatory1EmiratesId.trim().length > 0 &&
                form.signatory1ContactNo.trim().length > 0
            );
        }
        if (step === 2) {
            // All department contact fields are mandatory
            return (
                form.networkInsuranceName1.trim().length > 0 &&
                form.networkInsuranceEmail1.trim().length > 0 &&
                form.networkInsuranceContact1.trim().length > 0 &&
                form.accountsName1.trim().length > 0 &&
                form.accountsEmail1.trim().length > 0 &&
                form.accountsContact1.trim().length > 0 &&
                form.administrationName1.trim().length > 0 &&
                form.administrationEmail1.trim().length > 0 &&
                form.administrationContact1.trim().length > 0
            );
        }
        return true;

    };

    const renderStepContent = () => {
        switch (step) {
            case 1:
                return (
                    <div className="space-y-8 max-w-4xl mx-auto">
                        {/* Facility Licenses */}
                        <div className="bg-white border-2 border-gray-100 rounded-3xl p-6 md:p-8 shadow-xl">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                                    <FileText className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Facility License Information</h2>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-5 mb-6">
                                <div>
                                    <label className="block text-base font-semibold text-gray-800 mb-2">Health Authority License No *</label>
                                    <input
                                        name="facilityLicenseNo"
                                        value={form.facilityLicenseNo}
                                        onChange={handleChange}
                                        placeholder="e.g. HA-XXXXXX"
                                        required
                                        className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-base"
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-semibold text-gray-800 mb-2">Health Authority License Expiry date*</label>
                                    <input
                                        type="date"
                                        name="facilityLicenseExpiry"
                                        value={form.facilityLicenseExpiry}
                                        onChange={handleChange}
                                        min={new Date().toISOString().split('T')[0]}
                                        required
                                        className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-base"
                                    />
                                </div>
                            </div>

                            <div className="grid md:grid-cols-3 gap-5">
                                <div>
                                    <label className="block text-base font-semibold text-gray-800 mb-2">Trade License No*</label>
                                    <input
                                        name="tradeLicNo"
                                        value={form.tradeLicNo}
                                        onChange={handleChange}
                                        placeholder="Enter number"
                                        required
                                        className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-base"
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-semibold text-gray-800 mb-2">Trade License Name*</label>
                                    <input
                                        name="tradeLicName"
                                        value={form.tradeLicName}
                                        onChange={handleChange}
                                        placeholder="Trade License Name"
                                        required
                                        className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-base"
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-semibold text-gray-800 mb-2">Trade Licence Expiry Date*</label>
                                    <input
                                        type="date"
                                        name="tradeLicExpiry"
                                        value={form.tradeLicExpiry}
                                        onChange={handleChange}
                                        min={new Date().toISOString().split('T')[0]}
                                        required
                                        className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-base"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Authorized Signatory */}
                        <div className="bg-white border-2 border-gray-100 rounded-3xl p-6 md:p-8 shadow-xl">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                                    <Users className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Authorized Signatory</h2>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-3 gap-5 mb-6">
                                <div>
                                    <label className="block text-base font-semibold text-gray-800 mb-2">Full Name *</label>
                                    <input
                                        name="signatory1Name"
                                        value={form.signatory1Name}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-base"
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-semibold text-gray-800 mb-2">Designation*</label>
                                    <input
                                        name="signatory1Designation"
                                        value={form.signatory1Designation}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-base"
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-semibold text-gray-800 mb-2">Emirates ID*</label>
                                    <input
                                        type="text"
                                        name="signatory1EmiratesId"
                                        value={form.signatory1EmiratesId}
                                        onChange={handleChange}
                                        placeholder="784-1994-1234567-1"
                                        maxLength={18}
                                        required
                                        className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-base"

                                        onBlur={() => {
                                            if (
                                                form.signatory1EmiratesId &&
                                                !isValidEmiratesId(form.signatory1EmiratesId)
                                            ) {
                                                alert("Please enter a valid Emirates ID (784-YYYY-XXXXXXX-X)");
                                            }
                                        }}
                                    />

                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-base font-semibold text-gray-800 mb-2">Contact Number*</label>
                                    <input
                                        type="tel"
                                        name="signatory1ContactNo"
                                        value={form.signatory1ContactNo}
                                        onChange={handleChange}
                                        placeholder="971501234567"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        required
                                        className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-base"
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-semibold text-gray-800 mb-2">Remarks</label>
                                    <input
                                        name="signatory1Remarks"
                                        value={form.signatory1Remarks}
                                        onChange={handleChange}
                                        placeholder="Optional notes"
                                        className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-base"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-8 max-w-4xl mx-auto">
                        {[
                            {
                                title: 'Network & Insurance',
                                baseName: 'networkInsurance',
                                icon: User,
                                bgColor: 'bg-blue-100',
                                iconColor: 'text-blue-600',
                            },
                            {
                                title: 'Accounts',
                                baseName: 'accounts',
                                icon: Users,
                                bgColor: 'bg-emerald-100',
                                iconColor: 'text-emerald-600',
                            },
                            {
                                title: 'Administration',
                                baseName: 'administration',
                                icon: Users,
                                bgColor: 'bg-purple-100',
                                iconColor: 'text-purple-600',
                            },
                        ].map((dept, idx) => {
                            const baseName = dept.baseName;
                            return (
                                <div
                                    key={idx}
                                    className="bg-white border-2 border-gray-100 rounded-3xl p-6 md:p-8 shadow-xl"
                                >
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className={`w-12 h-12 ${dept.bgColor} rounded-2xl flex items-center justify-center`}>
                                            <dept.icon className={`w-6 h-6 ${dept.iconColor}`} />
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-bold text-gray-900">{dept.title} Department</h2>
                                            <p className="text-base text-gray-600 mt-1">Primary contact person</p>
                                        </div>
                                    </div>

                                    <div className="grid md:grid-cols-3 gap-5">
                                        <div>
                                            <label className="block text-base font-semibold text-gray-800 mb-2">Name *</label>
                                            <input
                                                type="text"
                                                name={`${baseName}Name1`}
                                                value={form[`${baseName}Name1`]}
                                                onChange={handleChange}
                                                placeholder="Full name"
                                                required
                                                className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-base"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-base font-semibold text-gray-800 mb-2">Email *</label>
                                            <input
                                                type="email"
                                                name={`${baseName}Email1`}
                                                value={form[`${baseName}Email1`]}
                                                onChange={handleChange}
                                                placeholder="name@example.com"
                                                required
                                                className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-base"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-base font-semibold text-gray-800 mb-2">Contact No *</label>
                                            <input
                                                type="tel"
                                                name={`${baseName}Contact1`}
                                                value={form[`${baseName}Contact1`]}
                                                onChange={handleChange}
                                                placeholder="971501234567"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                required
                                                className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-base"
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary/10 via-white to-primary/10 py-8 px-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-bold text-gray-900 mb-3">
                        Provider Empanelment
                    </h1>
                </div>

                {/* Progress Steps */}
                <div className="flex justify-center mb-10">
                    <div className="inline-flex gap-3 bg-gray-50 rounded-full shadow-lg p-2 border border-gray-200">
                        {steps.map((s) => (
                            <div
                                key={s.num}
                                className={`
                  flex items-center gap-3 px-6 py-3 rounded-full transition-all
                  ${step >= s.num ? 'bg-primary text-white shadow-md' : 'text-gray-500'}
                `}
                            >
                                <div className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center font-semibold">
                                    {step > s.num ? <Check size={18} /> : s.num}
                                </div>
                                <div className="hidden sm:block">
                                    <div className="font-medium">{s.title}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Main Form Card */}
                <div className="bg-white rounded-3xl shadow-xl p-6 md:p-10 border border-gray-100">
                    {renderStepContent()}
                </div>

                {/* Navigation */}
                <div className="flex justify-between items-center mt-10 max-w-3xl mx-auto">
                    <button
                        onClick={() => {
                            if (step === 1) {
                                window.location.href = '/#step-1';
                            } else {
                                setStep((s) => s - 1);
                            }
                        }}
                        className="flex items-center gap-3 px-8 py-3.5 rounded-full font-medium transition-all bg-white border-2 border-gray-300 hover:border-primary text-gray-700 hover:text-primary shadow-md"
                    >
                        <ArrowLeft size={18} />
                        {step === 1 ? 'Back to Facility Info' : 'Back'}
                    </button>

                    <button
                        onClick={() => {
                            if (step === 2) handleFinish();
                            else if (canProceed()) setStep((s) => s + 1);
                        }}
                        disabled={!canProceed()}
                        className={`
              flex items-center gap-3 px-9 py-3.5 rounded-full font-semibold text-white transition-all shadow-lg hover:shadow-xl
              ${canProceed()
                                ? 'bg-primary hover:bg-primary-dark'
                                : 'bg-gray-400 cursor-not-allowed'}
            `}
                    >
                        {step === 2 ? 'Finish & Continue' : 'Next Step'}
                        <ArrowRight size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
}